import { PRIMARY_BUTTON } from "@/constants";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import Input from "@/app/components/ui/Inputs/Input";
import InputWithEndIcon from "@/app/components/ui/Inputs/InputWithEndIcon";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import ViewMoreComponent from "@/app/components/ui/ViewMoreComponent";
import { useServiceDeskTicketListAPI } from "@/app/hooks/api_hooks/service_desk/useServiceDeskTicketListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { ROUTES } from "@/app/routes";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import RemarksComponent from "../RemarksComponent";

export interface IServiceTicketList {
  creation_date?: string;
  vetic_status?: string;
  search_keyword?: string;
  customer_user_id?: string;
}
export interface IServiceFiltersFormFields {
  issue_date: string;
  issue_status: string;
  search_ticket?: string;
}

const ServiceDeskissueList = () => {
  const { getRouteKey } = useRouter();
  const stringConstants = useStringConstants();
  const { NO_PWL_RESULTS } = useImageConstants();
  const {veticDateFormat } = useUtils();
  const [open,setOpen]=useState(false)
  const [serviceDetails,setServiceDetails]=useState(null)
  const navigate = useNavigate();

  const [query, setQuery] = useState<string>("");

  const { execute: fetchTicketList, ticketList } = useServiceDeskTicketListAPI();
  const isTickeLoading = useLoader("isServiceDeskTicketList");

  const formHook = useForm<IServiceFiltersFormFields>({
    mode: "onChange",
    defaultValues: {
      issue_status: "Pending",
      issue_date: format(new Date(Date.now()), stringConstants?.fieldDOBFormat),
    },
  });

  const providerListItem = [
    {
      id: "1",
      title: "Pending",
    },
    {
      id: "2",
      title: "Resolved",
    },
  ];

  const onSubmit = (data: IServiceFiltersFormFields) => {
    const constructedBody: IServiceTicketList = {
      creation_date: data.issue_date,
      vetic_status: data.issue_status,
    };
    fetchTicketList(constructedBody);
  };

  const handleSearch = () => {
    const constructedBody: IServiceTicketList = {
      search_keyword: query,
    };
    fetchTicketList(constructedBody);
  };

  useEffect(() => {
    const constructedBody: IServiceTicketList = {
      creation_date: format(new Date(Date.now()), stringConstants?.fieldDOBFormat),
      vetic_status: "Pending",
    };
    fetchTicketList(constructedBody);
  }, []);

  function handelBack(): void {
    const constructedBody: IServiceTicketList = {
      creation_date: format(new Date(Date.now()), stringConstants?.fieldDOBFormat),
      vetic_status: "Pending",
    };
    fetchTicketList(constructedBody);
    setQuery("");
  }

  const handelCLickToUserProfile = (id: string) => {
    navigate(`${ROUTES.ROOT.children.CUSTOMER_PROFILE.children.DETAILS_CUSTOMER.url}/appointments/${id}?from_om=true`);
  };

  return (
    <><div className="p-5 bg-white h-screen">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-lg p-3">
        <h2 className="text-black font-semibold">Need Help</h2>
        <Link
          className={`${PRIMARY_BUTTON} shrink-0`}
          to={getRouteKey("SERVECE_DESK_RAISE_ISSUE", "url")}
        >
          Raise Ticket
        </Link>
      </div>

      <form className="my-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4" onSubmit={formHook.handleSubmit(onSubmit)}>
        <PWLCalendarComponent
          errorMessages={[{ message: "Date is required", type: "required" }]}
          register={formHook.register("issue_date", {
            required: true,
          })}
          fieldError={formHook.formState.errors.issue_date}
          onSelectDate={(date) => {
            formHook.setValue("issue_date", format(date, stringConstants?.fieldDOBFormat));
          }}
          defaultSelectedDate={formHook.getValues("issue_date")}
          className="h-9 w-full"
          containerClassName="w-full sm:w-60"
        />
        <SearchableSelectMenu
          errorMessages={[{ message: "Clinic is provider", type: "required" }]}
          onSelectItem={(item) => {
            if (item) {
              formHook.setValue("issue_status", item.title);
            }
          }}
          fieldError={formHook.formState.errors.issue_status}
          register={formHook.register("issue_status", { required: true })}
          selectItems={providerListItem}
          placeholder="Select Ticket Status"
          showTooltips={false}
          showTypedErrors={false}
          showDropdownIcon={true}
          defaultSelected={providerListItem?.filter((oc) => oc.title === formHook.watch("issue_status"))[0]}
          listBoxClassName="w-full"
          className="text-gray-800 "
          containerClassName="w-full sm:w-60"
          showDropdownWhenNoData
        />{" "}
        <button className={`${PRIMARY_BUTTON} w-full sm:w-auto`} type="submit">
          Search
        </button>
      </form>
      <div className="py-2 w-full sm:w-60 [&_input]:h-9">
        <InputWithEndIcon
          className="text-xs w-full"
          placeholder="Search by Contact Number"
          value={query}
          onChange={(e: any) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.keyCode === 13) {
              handleSearch();
            }
          }}
          handelBack={handelBack}
          icon={
            query ? (
              <div className="cursor-pointer">
                {" "}
                <XMarkIcon className="w-4 h-4" />
              </div>
            ) : (
              <MagnifyingGlassIcon className="w-4 h-4" />
            )
          }
        />
      </div>
      {ticketList?.length > 0 ? (
        !isTickeLoading ? (
          <div className="-mx-4 my-3 overflow-x-auto sm:-mx-6 lg:-mx-8 ">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className=" shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg overflow-auto h-[700px]">
                <table className="min-w-full ">
                  <thead className="bg-gray-50">
                    <tr className="border-b">
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Customer
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 ">
                        Ticket ID
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Remarks
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Creation Date
                      </th>

                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Issue In
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Source
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Attachment
                      </th>
                      
                    </tr>
                  </thead>
                  <tbody className=" bg-white">
                    {ticketList?.map((item) => (
                      <tr key={item?.created_at} className="border-b">
                        <td
                          className="whitespace-nowrap px-3 py-3.5 text-sm font-medium  text-[#0068FF] underline cursor-pointer"
                          onClick={() => handelCLickToUserProfile(item?.user_id)}
                        >
                          {item?.user_name}
                        </td>
                        
                        <td className="whitespace-nowrap px-3 py-3.5 text-sm font-medium text-gray-900  ">
                          <p className="text-left  text-sm text-gray-900 p-3 pl-0">{item?.vetic_ticket_id}</p>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3.5 text-[10px] sm:text-xs 2xl:text-sm font-medium text-gray-900  ">
                        {item?.latest_private_comment===""&&item?.latest_public_comment===""?null:  <p className="text-left  text-sm text-blue-500 p-3 pl-0 cursor-pointer" 
                          onClick={()=>{
                            setServiceDetails(item);
                            setOpen(true)
                          }}
                          >View Remarks</p>}
                        </td>
                         

                        <td className="whitespace-nowrap px-3 py-3.5 text-sm font-medium text-gray-900 ">
                          {veticDateFormat(item?.created_at)}
                        </td>

                        <td className="text-xs text-left p-3 ">{item?.l1_heading}</td>
                        <td className="whitespace-nowrap px-3 py-3.5 text-sm font-medium text-gray-900 ">
                          {item?.source_of_ticket }
                        </td>
                        <td className="text-xs text-left p-3 ">
                          <p
                            className={`text-left  font-medium text-sm  ${item?.vetic_status.toLowerCase() === "pending" ? "text-[#B78811]" : "text-[#058E34]"}`}
                          >
                            {item?.vetic_status}
                          </p>
                        </td>

                        <td className="text-xs text-left p-3 ">
                          {item?.attachment_url?.length > 0 && (
                            <div>
                              {item?.attachment_url?.map((val, idx) => {
                                return (
                                  <a
                                    target="_blank"
                                    className="flex justify-start items-center gap-x-2 text-blue-400 underline pt-2"
                                    key={`emr_link:${idx + 1}`}
                                    href={val}
                                  >{`file ${idx + 1}`}</a>
                                );
                              })}
                            </div>
                          ) }
                        </td>
                      
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <SkeletonType1 />
        )
      ) : (
        <NoDataFoundComponent
          imageURL={NO_PWL_RESULTS}
          message="No Ticket Found"
          description="No Ticket found for the given filter, try again"
        />
      )}
    </div>
  {open&& <RemarksComponent open={open} setOpen={setOpen} serviceDetails={serviceDetails} setServiceDetails={setServiceDetails}/>}
  </>
  );
};
export default ServiceDeskissueList;
